/* oxlint-disable typescript/default-param-last */
/* oxlint-disable default-case */
import { type Action, combineReducers } from 'redux';

import {
  type Metadata,
  type RequestByKeyState,
  requestReducer,
  requestReducerByKey,
  type RequestState,
  type Series,
} from '../shared';

import { type Actions, ActionTypes } from './actions';
import {
  type Account,
  type AuditLogEntries,
  type AuditLogEntry,
  type AuditLogEntryDetail,
  type CustomRole,
  type IEapSegmentsResponse,
  type LegacySubscription,
  MAX_RECENTLY_VIEWED_ACCOUNTS,
  type Member,
  type MemberSessions,
  type MembersPaginationData,
  type SavedAccount,
  type Subscription,
  type SubscriptionUsage,
} from './model';
import { getAccountsFromLocalStorage, localStorageKeys } from './storageUtils';

type IFavoriteAccountState = SavedAccount[];

// Dedupe favorites by account id
function dedupedFavoritedAccounts(accounts: SavedAccount[]): SavedAccount[] {
  return Array.from(new Map(accounts.map((a) => [a.id, a])).values());
}

function manageFavoritedAccounts(state: IFavoriteAccountState = [], action: Actions) {
  const favorited: SavedAccount[] = getAccountsFromLocalStorage(localStorageKeys.FAVORITED);

  if (action.type === ActionTypes.ADD_FAVORITED_ACCOUNT) {
    const favoritedAccounts: SavedAccount[] = [action.favoritedAccount, ...favorited];
    const deduped = dedupedFavoritedAccounts(favoritedAccounts);
    localStorage.setItem(localStorageKeys.FAVORITED, JSON.stringify(deduped));
    return deduped;
  }

  if (action.type === ActionTypes.REMOVE_FAVORITED_ACCOUNT) {
    const favoritedAccounts: SavedAccount[] = favorited.filter(
      (favorite) => favorite.id !== action.favoritedAccount.id,
    );
    const deduped = dedupedFavoritedAccounts(favoritedAccounts);
    localStorage.setItem(localStorageKeys.FAVORITED, JSON.stringify(deduped));
    return deduped;
  }

  if (action.type === ActionTypes.GET_FAVORITED_ACCOUNT) {
    const favoritedAccounts: SavedAccount[] = dedupedFavoritedAccounts(favorited);
    if (favoritedAccounts.length !== favorited.length) {
      localStorage.setItem(localStorageKeys.FAVORITED, JSON.stringify(favoritedAccounts));
    }
    return favoritedAccounts;
  }
  return state;
}

type IEntitiesState = {
  [id: string]: Account;
};

type IRecentlyViewedAccountsState = SavedAccount[];

const addRecentlyViewedAccounts = (state: IRecentlyViewedAccountsState = [], action: Actions) => {
  if (action.type === ActionTypes.ADD_ACCOUNT_TO_RECENTLY_VIEWED) {
    const recentlyViewed: SavedAccount[] = getAccountsFromLocalStorage(localStorageKeys.RECENTLY_VIEWED);
    // filter out account to be added from the existing list in the event it's already in recent
    const filterCurrentFromRecent: SavedAccount[] = recentlyViewed.filter(
      (recent) => recent.id !== action.recentlyViewedAccount.id,
    );
    const recentlyViewedAccounts: SavedAccount[] = [action.recentlyViewedAccount]
      .concat(filterCurrentFromRecent)
      .slice(0, MAX_RECENTLY_VIEWED_ACCOUNTS);
    localStorage.setItem(localStorageKeys.RECENTLY_VIEWED, JSON.stringify(recentlyViewedAccounts));
    return recentlyViewedAccounts;
  }
  return state;
};

const entities = (state: IEntitiesState = {}, action: Actions) => {
  if (action.type === ActionTypes.FETCH_ACCOUNTS_FULFILLED) {
    return action.response.entities.accounts || {};
  }

  if (action.type === ActionTypes.FETCH_ACCOUNT_FULFILLED) {
    return {
      ...state,
      ...action.response.entities.accounts,
    };
  }

  if (action.type === ActionTypes.RECEIVE_ACCOUNT_RESULTS) {
    const accountsReturned = action.response.entities.accounts;

    const newState = { ...state };

    if (accountsReturned) {
      Object.entries(accountsReturned).forEach(([key, value]) => {
        if (state[key]) {
          newState[key] = { ...state[key], ...value };
        } else {
          newState[key] = value;
        }
      });
    }

    return {
      ...newState,
    };
  }

  return state;
};

type IMembersPaginationDataState = {
  [id: string]: MembersPaginationData;
};

const membersPaginationData = (state: IMembersPaginationDataState = {}, action: Actions) => {
  if (action.type === ActionTypes.FETCH_MEMBERS_PAGINATION_DATA_FULFILLED) {
    const { result } = action.response;
    return {
      ...state,
      [action.account._id]: {
        links: result._links,
        totalCount: result.totalCount,
      },
    };
  }

  return state;
};

type IMembersByAccountState = {
  [id: string]: {
    [id: string]: Member;
  };
};

const membersByAccount = (state: IMembersByAccountState = {}, action: Actions) => {
  if (action.type === ActionTypes.FETCH_MEMBERS_FULFILLED) {
    return {
      ...state,
      [action.account._id]: {
        ...action.response.entities.members,
      },
    };
  }

  if (action.type === ActionTypes.DISABLE_MFA_FULFILLED) {
    const { response } = action;
    return {
      ...state,
      [action.account._id]: {
        ...state[action.account._id],
        [response._id]: response,
      },
    };
  }

  return state;
};

const membersForExportByAccount = (state: IMembersByAccountState = {}, action: Actions) => {
  if (action.type === ActionTypes.FETCH_MEMBERS_FOR_EXPORT_FULFILLED) {
    return {
      ...state,
      [action.account._id]: {
        ...action.response.entities.members,
      },
    };
  }

  return state;
};

type IMemberSessionsByAccountState = {
  [id: string]: {
    [id: string]: MemberSessions;
  };
};

const memberSessionsByAccount = (state: IMemberSessionsByAccountState = {}, action: Actions) => {
  if (action.type === ActionTypes.FETCH_MEMBER_SESSIONS_FULFILLED) {
    return {
      ...state,
      [action.account._id]: {
        ...state[action.account._id],
        ...action.response.entities.memberSessions,
      },
    };
  }

  return state;
};

type IAuditLogEntriesByAccountState = {
  [id: string]: AuditLogEntries;
} & {
  entries?: AuditLogEntries;
  isFetching?: boolean;
};

const auditLogEntriesByAccount = (state: IAuditLogEntriesByAccountState = {}, action: Actions) => {
  switch (action.type) {
    case ActionTypes.FETCH_AUDIT_LOG_ENTRIES:
      if (!action.nextUrl) {
        return {
          ...state,
          [action.account._id]: {
            isFetching: true,
            entries: [],
            items: [],
            _links: {},
          },
        };
      }
      return {
        ...state,
        [action.account._id]: {
          ...state[action.account._id],
          isFetching: true,
        },
      };

    case ActionTypes.FETCH_AUDIT_LOG_ENTRIES_FULFILLED: {
      const allEntries: AuditLogEntry[] = state[action.account._id].entries;
      const newEntries: AuditLogEntry[] = action.response && action.response.items;
      newEntries.map((entry) => allEntries.push(entry));
      return {
        ...state,
        [action.account._id]: {
          ...state[action.account._id],
          isFetching: false,
          ...action.response,
        },
      };
    }
    case ActionTypes.FETCH_AUDIT_LOG_ENTRIES_FAILED:
      return {
        ...state,
        [action.account._id]: {
          ...state[action.account._id],
          isFetching: false,
        },
      };
  }
  return state;
};

type IAuditLogEntryDetailByAccountState = {
  [id: string]: AuditLogEntryDetail;
} & {
  isFetching?: boolean;
};

const initialEntryDetailState = {
  isFetching: false,
} as IAuditLogEntryDetailByAccountState;

const auditLogEntryDetailByAccount = (
  state: IAuditLogEntryDetailByAccountState = initialEntryDetailState,
  action: Actions,
) => {
  switch (action.type) {
    case ActionTypes.FETCH_AUDIT_LOG_ENTRY_DETAIL:
      return {
        ...state,
        [action.account._id]: {
          // clears optional fields and handles loading
          ...state[action.account._id],
          parent: undefined,
          token: undefined,
          comment: undefined,
          delta: undefined,
          previousVersion: undefined,
          currentVersion: undefined,
          isFetching: true,
        },
      };
    case ActionTypes.FETCH_AUDIT_LOG_ENTRY_DETAIL_FULFILLED:
      return {
        ...state,
        [action.account._id]: {
          ...state[action.account._id],
          isFetching: false,
          ...action.response,
        },
      };
  }
  return state;
};

type ICustomRolesByAccountState = {
  [id: string]: {
    [id: string]: CustomRole;
  };
};

const customRolesByAccount = (state: ICustomRolesByAccountState = {}, action: Actions) => {
  if (action.type === ActionTypes.FETCH_CUSTOM_ROLES_FULFILLED) {
    return {
      ...state,
      [action.account._id]: {
        ...state[action.account._id],
        ...action.response.entities.customRoles,
      },
    };
  }

  return state;
};

type ILegacySubscriptionsByAccountState = {
  [id: string]: LegacySubscription;
};

type ISubscriptionsByAccountState = {
  [id: string]: Subscription;
};

type ISubscriptionUsageByAccount = {
  [id: string]: SubscriptionUsage;
};

const subscriptionByAccount = (state: ISubscriptionsByAccountState = {}, action: Actions) => {
  switch (action.type) {
    case ActionTypes.FETCH_SUBSCRIPTION_FULFILLED:
      return {
        ...state,
        [action.account._id]: action.response,
      };
    case ActionTypes.SUBSCRIBE_FULFILLED:
    case ActionTypes.UPDATE_TRIAL_END_FULFILLED:
    case ActionTypes.UPDATE_GRACE_END_FULFILLED:
    case ActionTypes.UPDATE_ENTERPRISE_CAMPAIGN_DATES_FULFILLED:
      return {
        ...state,
        [action.account._id]: action.updatedSubscription,
      };
    case ActionTypes.UPDATE_GUARDIAN_CAMPAIGN_DATES_FULFILLED:
      return {
        ...state,
        [action.account._id]: action.updatedSubscription,
      };
    default:
      return state;
  }
};

type ITrialEndUpdatesByAccountState = {
  [id: string]: RequestState;
};

const trialEndUpdateByAccount = requestReducerByKey(
  [ActionTypes.UPDATE_TRIAL_END, ActionTypes.UPDATE_TRIAL_END_FULFILLED, ActionTypes.UPDATE_TRIAL_END_FAILED],
  (action: IRequestByAccountAction) => action.account._id,
);

const graceEndUpdateByAccount = requestReducerByKey(
  [ActionTypes.UPDATE_GRACE_END, ActionTypes.UPDATE_GRACE_END_FULFILLED, ActionTypes.UPDATE_GRACE_END_FAILED],
  (action: IRequestByAccountAction) => action.account._id,
);

const legacySubscriptionByAccount = (state: ILegacySubscriptionsByAccountState = {}, action: Actions) => {
  switch (action.type) {
    case ActionTypes.FETCH_LEGACY_SUBSCRIPTION_FULFILLED:
      return {
        ...state,
        [action.account._id]: action.response,
      };
    case ActionTypes.UPDATE_LEGACY_TRIAL_END_FULFILLED:
    case ActionTypes.UPDATE_LEGACY_GRACE_END_FULFILLED:
      return {
        ...state,
        [action.account._id]: action.updatedSubscription,
      };
    default:
      return state;
  }
};

type ILegacyTrialEndUpdatesByAccountState = {
  [id: string]: RequestState;
};

const legacyTrialEndUpdateByAccount = requestReducerByKey(
  [
    ActionTypes.UPDATE_LEGACY_TRIAL_END,
    ActionTypes.UPDATE_LEGACY_TRIAL_END_FULFILLED,
    ActionTypes.UPDATE_LEGACY_TRIAL_END_FAILED,
  ],
  (action: IRequestByAccountAction) => action.account._id,
);

const legacyGraceEndUpdateByAccount = requestReducerByKey(
  [
    ActionTypes.UPDATE_LEGACY_GRACE_END,
    ActionTypes.UPDATE_LEGACY_GRACE_END_FULFILLED,
    ActionTypes.UPDATE_LEGACY_GRACE_END_FAILED,
  ],
  (action: IRequestByAccountAction) => action.account._id,
);

const request = requestReducer([
  ActionTypes.FETCH_ACCOUNTS,
  ActionTypes.FETCH_ACCOUNTS_FULFILLED,
  ActionTypes.FETCH_ACCOUNTS_FAILED,
]);

type IRequestByIdAction = {
  accountId: string;
} & Action;

const requestsById = requestReducerByKey(
  [ActionTypes.FETCH_ACCOUNT, ActionTypes.FETCH_ACCOUNT_FULFILLED, ActionTypes.FETCH_ACCOUNT_FAILED],
  (action: IRequestByIdAction) => action.accountId,
);

type IRequestByAccountAction = {
  account: Account;
} & Action;

const subscriptionRequestByAccountId = requestReducerByKey(
  [
    ActionTypes.FETCH_LEGACY_SUBSCRIPTION,
    ActionTypes.FETCH_LEGACY_SUBSCRIPTION_FULFILLED,
    ActionTypes.FETCH_LEGACY_SUBSCRIPTION_FAILED,
  ],
  (action: IRequestByAccountAction) => action.account._id,
);

const membersRequestByAccountId = requestReducerByKey(
  [ActionTypes.FETCH_MEMBERS, ActionTypes.FETCH_MEMBERS_FULFILLED, ActionTypes.FETCH_MEMBERS_FAILED],
  (action: IRequestByAccountAction) => action.account._id,
);

const memberSessionsRequestByAccountId = requestReducerByKey(
  [
    ActionTypes.FETCH_MEMBER_SESSIONS,
    ActionTypes.FETCH_MEMBER_SESSIONS_FULFILLED,
    ActionTypes.FETCH_MEMBER_SESSIONS_FAILED,
  ],
  (action: IRequestByAccountAction) => action.account._id,
);

const auditLogEntriesRequestByAccountId = requestReducerByKey(
  [
    ActionTypes.FETCH_AUDIT_LOG_ENTRIES,
    ActionTypes.FETCH_AUDIT_LOG_ENTRIES_FULFILLED,
    ActionTypes.FETCH_AUDIT_LOG_ENTRIES_FAILED,
  ],
  (action: IRequestByAccountAction) => action.account._id,
);

const auditLogEntryDetailRequestByAccountId = requestReducerByKey(
  [
    ActionTypes.FETCH_AUDIT_LOG_ENTRY_DETAIL,
    ActionTypes.FETCH_AUDIT_LOG_ENTRY_DETAIL_FULFILLED,
    ActionTypes.FETCH_AUDIT_LOG_ENTRY_DETAIL_FAILED,
  ],
  (action: IRequestByAccountAction) => action.account._id,
);

const customRolesRequestByAccountId = requestReducerByKey(
  [ActionTypes.FETCH_CUSTOM_ROLES, ActionTypes.FETCH_CUSTOM_ROLES_FULFILLED, ActionTypes.FETCH_CUSTOM_ROLES_FAILED],
  (action: IRequestByAccountAction) => action.account._id,
);

type ISearchState = {
  isFetching: boolean;
  q: string;
  results: string[];
};

const initialSearchState = {
  isFetching: false,
  q: '',
  results: [],
};

const search = (state: ISearchState = initialSearchState, action: Actions) => {
  switch (action.type) {
    case ActionTypes.SEARCH_ACCOUNTS:
      return {
        ...state,
        isFetching: true,
        q: action.q,
      };
    case ActionTypes.RECEIVE_ACCOUNT_RESULTS:
      return {
        ...state,
        isFetching: false,
        q: action.q,
        results: action.response.result.items,
      };
    case ActionTypes.CLEAR_ACCOUNT_RESULTS:
      return initialSearchState;
    default:
      return state;
  }
};

type IAccountMetricUsageState = {
  metadata: Metadata;
  series: Series;
};

const initialAccountUsageState = {
  _links: {},
  metadata: [],
  series: [],
};

const subscriptionUsageFetchRequest = requestReducer([
  ActionTypes.FETCH_SUBSCRIPTION_USAGE,
  ActionTypes.FETCH_SUBSCRIPTION_USAGE_FULFILLED,
  ActionTypes.FETCH_SUBSCRIPTION_USAGE_FAILED,
]);

export const subscription = (state: Subscription | null = null, action: Actions) => {
  switch (action.type) {
    case ActionTypes.FETCH_SUBSCRIPTION_FULFILLED:
      return action.response;
    case ActionTypes.SUBSCRIBE_FULFILLED:
      return action.updatedSubscription;
    default:
      return state;
  }
};

const highAccountUsageFetchRequest = requestReducer([
  ActionTypes.REQUEST_HIGH_ACCOUNT_USAGE,
  ActionTypes.REQUEST_HIGH_ACCOUNT_USAGE_FULFILLED,
  ActionTypes.REQUEST_HIGH_ACCOUNT_USAGE_FAILED,
]);

export const highAccountUsage = (state: IAccountMetricUsageState = initialAccountUsageState, action: Actions) => {
  switch (action.type) {
    case ActionTypes.REQUEST_HIGH_ACCOUNT_USAGE_FULFILLED:
      return action.data;
    default:
      return state;
  }
};

export const subscriptionUsage = (state: SubscriptionUsage | null = null, action: Actions) => {
  switch (action.type) {
    case ActionTypes.FETCH_SUBSCRIPTION_USAGE_FULFILLED:
      return action.response;
    default:
      return state;
  }
};

export const subscriptionUsageByAccount = (state: ISubscriptionUsageByAccount = {}, action: Actions) => {
  switch (action.type) {
    case ActionTypes.FETCH_SUBSCRIPTION_USAGE_FULFILLED:
      return {
        ...state,
        [action.account._id]: action.response,
      };
    default:
      return state;
  }
};
type IEapSegmentsByAccountState = {
  [id: string]: IEapSegmentsResponse;
};

// const eapSegmentsByAccount = (state: IEapSegmentsByAccountState = {}, action: Actions) => {
//   if (action.type === ActionTypes.FETCH_EAP_SEGMENTS_FULFILLED) {
//     return {
//       ...state,
//       [action.account._id]: action.response,
//     };
//   }
//   return state;
// };

// const eapSegmentsRequestByAccountId = requestReducerByKey(
//   [ActionTypes.FETCH_EAP_SEGMENTS, ActionTypes.FETCH_EAP_SEGMENTS_FULFILLED, ActionTypes.FETCH_EAP_SEGMENTS_FAILED],
//   (action: IRequestByAccountAction) => action.account._id,
// );

export const subscriptionFetchRequest = requestReducer([
  ActionTypes.FETCH_SUBSCRIPTION,
  ActionTypes.FETCH_SUBSCRIPTION_FULFILLED,
  ActionTypes.FETCH_SUBSCRIPTION_FAILED,
]);

export const subscriptionUpdateRequest = requestReducer([
  ActionTypes.SUBSCRIBE,
  ActionTypes.SUBSCRIBE_FULFILLED,
  ActionTypes.SUBSCRIBE_FAILED,
  ActionTypes.CANCEL_SUBSCRIPTION,
  ActionTypes.CANCEL_SUBSCRIPTION_FULFILLED,
  ActionTypes.CANCEL_SUBSCRIPTION_FAILED,
  ActionTypes.UPDATE_TRIAL_END,
  ActionTypes.UPDATE_TRIAL_END_FULFILLED,
  ActionTypes.UPDATE_TRIAL_END_FAILED,
  ActionTypes.UPDATE_GRACE_END,
  ActionTypes.UPDATE_GRACE_END_FULFILLED,
  ActionTypes.UPDATE_GRACE_END_FAILED,
  ActionTypes.UPDATE_ENTERPRISE_CAMPAIGN_DATES,
  ActionTypes.UPDATE_ENTERPRISE_CAMPAIGN_DATES_FULFILLED,
  ActionTypes.UPDATE_ENTERPRISE_CAMPAIGN_DATES_FAILED,
  ActionTypes.UPDATE_GUARDIAN_CAMPAIGN_DATES,
  ActionTypes.UPDATE_GUARDIAN_CAMPAIGN_DATES_FULFILLED,
  ActionTypes.UPDATE_GUARDIAN_CAMPAIGN_DATES_FAILED,
]);

export const setMfaDisabledRequest = requestReducer([
  ActionTypes.DISABLE_MFA,
  ActionTypes.DISABLE_MFA_FULFILLED,
  ActionTypes.DISABLE_MFA_FAILED,
]);

export type ISearchMembersState = {
  isFetching: boolean;
  error?: string | object;
  query: string;
  results: { [id: string]: Member };
  totalCount?: number;
  _links?: {
    self: { href: string; type: string };
    [key: string]: { href: string; type: string };
  };
};

const initialSearchMembersState: ISearchMembersState = {
  isFetching: false,
  error: undefined,
  query: '',
  results: {},
  totalCount: 0,
  _links: { self: { href: '', type: '' } },
};

export const searchMembers = (
  state: ISearchMembersState = initialSearchMembersState,
  action: Actions,
): ISearchMembersState => {
  switch (action.type) {
    case ActionTypes.SEARCH_MEMBERS:
      return {
        ...state,
        isFetching: true,
        query: action.query || '',
      };
    case ActionTypes.SEARCH_MEMBERS_FULFILLED:
      return {
        ...state,
        isFetching: false,
        results:
          Object.fromEntries(
            // oxlint-disable-next-line typescript/no-explicit-any -- Required for MemberResponse type compatibility
            ((action.response as any).items ?? []).map((member: Member) => [member._id, member]),
          ) || {},
        // oxlint-disable-next-line typescript/no-explicit-any -- Required for MemberResponse type compatibility
        totalCount: (action.response as any).totalCount,
        // oxlint-disable-next-line typescript/no-explicit-any -- Required for MemberResponse type compatibility
        _links: (action.response as any)._links,
      };
    case ActionTypes.SEARCH_MEMBERS_FAILED:
      return {
        ...state,
        isFetching: false,
        error: action.error?.message,
      };
    default:
      return state;
  }
};

export const reducer = combineReducers({
  manageFavoritedAccounts,
  entities,
  addRecentlyViewedAccounts,
  membersPaginationData,
  membersByAccount,
  membersForExportByAccount,
  membersRequestByAccountId,
  memberSessionsByAccount,
  memberSessionsRequestByAccountId,
  highAccountUsage,
  auditLogEntriesByAccount,
  auditLogEntriesRequestByAccountId,
  auditLogEntryDetailByAccount,
  auditLogEntryDetailRequestByAccountId,
  customRolesByAccount,
  customRolesRequestByAccountId,
  legacySubscriptionByAccount,
  subscriptionByAccount,
  subscriptionRequestByAccountId,
  legacyTrialEndUpdateByAccount,
  legacyGraceEndUpdateByAccount,
  graceEndUpdateByAccount,
  trialEndUpdateByAccount,
  request,
  requestsById,
  search,
  subscription,
  subscriptionFetchRequest,
  subscriptionUsageFetchRequest,
  highAccountUsageFetchRequest,
  subscriptionUpdateRequest,
  subscriptionUsage,
  subscriptionUsageByAccount,
  setMfaDisabledRequest,
  // eapSegmentsByAccount,
  // eapSegmentsRequestByAccountId,
  searchMembers,
});

export type UsageState = Readonly<{
  usageRequest: RequestState;
  data: IAccountMetricUsageState;
}>;

export type AccountState = Readonly<{
  manageFavoritedAccounts: IFavoriteAccountState;
  entities: IEntitiesState;
  addRecentlyViewedAccounts: IRecentlyViewedAccountsState;
  membersPaginationData: IMembersPaginationDataState;
  membersByAccount: IMembersByAccountState;
  membersForExportByAccount: IMembersByAccountState;
  membersRequestByAccountId: RequestByKeyState;
  memberSessionsByAccount: IMemberSessionsByAccountState;
  memberSessionsRequestByAccountId: RequestByKeyState;
  auditLogEntriesByAccount: IAuditLogEntriesByAccountState;
  auditLogEntriesRequestByAccountId: RequestByKeyState;
  auditLogEntryDetailByAccount: IAuditLogEntryDetailByAccountState;
  auditLogEntryDetailRequestByAccountId: RequestByKeyState;
  customRolesByAccount: ICustomRolesByAccountState;
  customRolesRequestByAccountId: RequestByKeyState;
  legacySubscriptionByAccount: ILegacySubscriptionsByAccountState;
  subscriptionByAccount: ISubscriptionsByAccountState;
  subscriptionRequestByAccountId: RequestByKeyState;
  trialEndUpdateByAccount: ITrialEndUpdatesByAccountState;
  legacyTrialEndUpdateByAccount: ILegacyTrialEndUpdatesByAccountState;
  request: RequestState;
  requestsById: RequestByKeyState;
  search: ISearchState;
  subscription: Subscription | null;
  subscriptionUsage: SubscriptionUsage | null;
  highAccountUsage: IAccountMetricUsageState;
  highAccountUsageFetchRequest: RequestState;
  subscriptionUsageByAccount: ISubscriptionUsageByAccount;
  subscriptionFetchRequest: RequestState;
  subscriptionUsageFetchRequest: RequestState;
  subscriptionUpdateRequest: RequestState;
  setMfaDisabledRequest: RequestState;
  eapSegmentsByAccount: IEapSegmentsByAccountState;
  eapSegmentsRequestByAccountId: RequestByKeyState;
  searchMembers: ISearchMembersState;
}>;
